import { useCallback, useEffect, useRef, useState } from "react";
import {
  MAX_PHOTOS_PER_ITEM,
  STATUS_LABELS,
  photoPath,
  priceFromCents,
  thumbPath,
  type Item,
} from "@iltocat/shared";
import { ApiError, api, plural } from "../lib/api.js";
import { isImage, preparePhoto } from "../lib/photos.js";
import { usePhotoSize } from "../lib/usePhotoSize.js";
import type { Taxonomy } from "../lib/useTaxonomy.js";
import type { Vocabulary } from "../lib/useVocabulary.js";
import { EmptyState } from "../components/EmptyState.js";
import { blankField } from "../components/FieldsEditor.js";
import { Icon } from "../components/Icon.js";
import { ItemForm, draftToRequest, emptyDraft, type CostHint, type Draft } from "../components/ItemForm.js";
import { Menu, MenuItem } from "@vcnet/ui";
import { Notice } from "../components/Notice.js";
import { PageHeader } from "../components/PageHeader.js";
import { revealPanel } from "../components/Panel.js";
import { StockPanel, useStockItem } from "../components/StockPanel.js";
import { useToast } from "../components/Toast.js";

/**
 * One item: what it looks like, what is known about it, and what to do with it
 * next (fill it in with a chat model, list it, or delete it).
 *
 * This is where the "needs details" pile gets emptied, so the form is fully
 * open rather than behind a disclosure: the capture screen is for speed, this
 * one is for finishing. Saving sends the whole form because the whole form is
 * on screen; nothing here is a partial patch racing with anything else. The
 * Save bar appears only once something has changed, so an untouched item has
 * nothing nagging at the bottom of the screen.
 *
 * The photos are a gallery: one large, the rest as thumbnails that choose which
 * is large, and the tools (reorder, make cover, open, delete) act on the large
 * one. Big targets on a phone, and no arrows crammed onto 96px thumbnails.
 *
 * Stage 5's sharing settled for last-write-wins over real-time collaboration,
 * on the condition that who touched an item last is visible (see
 * docs/notes/decisions.md). This screen is where that has to show, because it
 * is the one place somebody is about to save over it.
 */

const touchedAt = (seconds: number): string =>
  new Date(seconds * 1000).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });

const draftOf = (item: Item): Draft => ({
  title: item.title,
  description: item.description,
  notes: item.notes,
  locationId: item.locationId,
  categoryId: item.categoryId,
  price: priceFromCents(item.priceCents),
  condition: item.condition,
  status: item.status,
  quantity: String(item.quantity),
  tags: item.tags,
  fields: item.fields.map((field) =>
    blankField({
      label: field.label,
      value: field.value,
      type: field.type,
      group: field.group,
      visibility: field.visibility,
      saved: true,
    }),
  ),
});

/** What would be sent, as a string: two drafts that would save the same thing are the same. */
const signature = (draft: Draft): string => JSON.stringify(draftToRequest(draft));

export function ItemScreen({
  id,
  taxonomy,
  vocabulary,
  onBack,
  onGone,
  onEnrich,
  onListing,
  onLabel,
  onStorage,
  stock = false,
}: {
  id: string;
  /** In a stock inventory: show the item's stock record under the form. */
  stock?: boolean;
  taxonomy: Taxonomy;
  vocabulary: Vocabulary;
  onBack: () => void;
  /** The item was deleted, so there is nothing left to show. */
  onGone: () => void;
  /** Open enrichment with this item chosen. */
  onEnrich: () => void;
  /** Open the listing screen for this item. */
  onListing: () => void;
  onLabel: () => void;
  onStorage: (usedBytes: number) => void;
}) {
  const [item, setItem] = useState<Item | null>(null);
  const [draft, setDraft] = useState<Draft>(emptyDraft);
  const [baseline, setBaseline] = useState("");
  const [state, setState] = useState<"loading" | "ready" | "missing">("loading");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [shown, setShown] = useState(0);
  const [photoSize, setPhotoSize] = usePhotoSize();
  const [dragging, setDragging] = useState(false);
  const camera = useRef<HTMLInputElement>(null);
  const toast = useToast();
  const stockItem = useStockItem(item, stock);

  const adopt = useCallback((next: Item) => {
    const fresh = draftOf(next);
    setItem(next);
    setDraft(fresh);
    setBaseline(signature(fresh));
  }, []);

  useEffect(() => {
    let live = true;
    void api.items
      .get(id)
      .then(({ item: found }) => {
        if (!live) return;
        adopt(found);
        setState("ready");
      })
      .catch(() => {
        if (live) setState("missing");
      });
    return () => {
      live = false;
    };
  }, [id, adopt]);

  const dirty = item !== null && signature(draft) !== baseline;

  async function save() {
    setBusy(true);
    setError(null);
    try {
      const { item: saved } = await api.items.update(id, draftToRequest(draft));
      adopt(saved);
      taxonomy.remember(saved.locationId, saved.categoryId);
      // The counts on the location tree moved, and this is the screen that
      // moved them.
      void taxonomy.reload();
      // Tags are made by wearing them, so the suggestion list moved too.
      void vocabulary.reloadTags();
      toast("Saved.");
    } catch (problem) {
      setError(problem instanceof ApiError ? problem.message : "That did not save.");
    } finally {
      setBusy(false);
    }
  }

  async function addPhotos(files: FileList | null) {
    if (!files || files.length === 0 || !item || busy) return;
    const room = MAX_PHOTOS_PER_ITEM - item.photos.length;
    const images = [...files].filter(isImage);
    const wanted = images.slice(0, Math.max(room, 0));
    if (wanted.length === 0) {
      setError(images.length === 0 ? "Those were not photos." : "That is as many photos as one item holds.");
      return;
    }
    setBusy(true);
    setError(
      wanted.length < images.length
        ? `Only ${plural(room, "more photo")} fit on this item, so the rest were left out.`
        : null,
    );
    try {
      for (const file of wanted) {
        const prepared = await preparePhoto(file);
        const { usedBytes } = await api.items.addPhoto(id, prepared);
        URL.revokeObjectURL(prepared.previewUrl);
        onStorage(usedBytes);
      }
      const { item: fresh } = await api.items.get(id);
      // Only the photos: a fresh copy of everything would throw away edits
      // somebody has typed and not saved yet.
      setItem((current) => (current ? { ...current, photos: fresh.photos } : fresh));
      setShown(fresh.photos.length - 1);
    } catch (problem) {
      setError(problem instanceof ApiError ? problem.message : "That photo did not upload.");
    } finally {
      setBusy(false);
    }
  }

  /**
   * Sends a whole new order. The server is the one source of truth for
   * `sortOrder` (see docs/notes/decisions.md on why the cover is the lowest one
   * rather than a flag), so this adopts whatever it answers with rather than
   * trusting the order computed here.
   */
  async function reorder(order: string[], showAt: number) {
    setBusy(true);
    setError(null);
    try {
      const { photos } = await api.items.reorderPhotos(id, order);
      setItem((current) => (current ? { ...current, photos } : current));
      setShown(showAt);
    } catch (problem) {
      setError(problem instanceof ApiError ? problem.message : "That did not save.");
    } finally {
      setBusy(false);
    }
  }

  function move(direction: -1 | 1) {
    if (!item) return;
    const order = item.photos.map((photo) => photo.id);
    const to = shown + direction;
    if (to < 0 || to >= order.length) return;
    [order[shown], order[to]] = [order[to]!, order[shown]!];
    void reorder(order, to);
  }

  function makeCover() {
    if (!item || shown === 0) return;
    const order = item.photos.map((photo) => photo.id);
    const [chosen] = order.splice(shown, 1);
    void reorder([chosen!, ...order], 0);
  }

  async function removePhoto(photoId: string) {
    setBusy(true);
    try {
      const { usedBytes } = await api.photos.remove(photoId);
      onStorage(usedBytes);
      setItem((current) =>
        current ? { ...current, photos: current.photos.filter((photo) => photo.id !== photoId) } : current,
      );
      setShown((current) => Math.max(0, current - 1));
    } catch (problem) {
      setError(problem instanceof ApiError ? problem.message : "That photo did not delete.");
    } finally {
      setBusy(false);
    }
  }

  async function deleteItem() {
    // A native confirm rather than a modal: it is one question, it is
    // unmistakably a browser dialog, and a hand-rolled one here would be the
    // only modal in the app.
    // Stock that went out but was never billed can be deleted (a mistake
    // undone), and its history and anything waiting to bill go with it.
    const hasStock = stockItem.detail && stockItem.detail !== "none";
    const question = hasStock
      ? "Delete this item, its photos and its stock history? Anything it had waiting to bill goes too. To keep the history, retire it as “Entered by mistake” instead. This cannot be undone."
      : "Delete this item and its photos? This cannot be undone.";
    if (!window.confirm(question)) return;

    setBusy(true);
    try {
      const { usedBytes } = await api.items.remove(id);
      onStorage(usedBytes);
      void taxonomy.reload();
      onGone();
    } catch (problem) {
      setError(problem instanceof ApiError ? problem.message : "That did not delete.");
      setBusy(false);
    }
  }

  const back = { label: "Items", onClick: onBack };

  if (state === "loading") {
    return (
      <>
        <PageHeader title={<span className="skeleton-line" />} back={back} />
        <div className="item-layout">
          <div className="gallery-main skeleton" />
          <div className="panel skeleton-panel" />
        </div>
      </>
    );
  }

  if (state === "missing" || !item) {
    return (
      <>
        <PageHeader title="Not found" back={back} />
        <EmptyState icon="box" title="That item is not here">
          It was deleted, or it belongs to another inventory.
        </EmptyState>
      </>
    );
  }

  const unitCost =
    stockItem.detail && stockItem.detail !== "none" ? stockItem.detail.stock.unitCostCents : null;
  const costHint: CostHint | null =
    unitCost !== null && stockItem.markup !== null ? { costCents: unitCost, markup: stockItem.markup } : null;

  const current = item.photos[Math.min(shown, item.photos.length - 1)];
  const at = current ? item.photos.indexOf(current) : 0;

  const full = item.photos.length >= MAX_PHOTOS_PER_ITEM;

  const addButton = (labelled: boolean, className: string) => (
    <label className={className} title="Add photos">
      <Icon name={labelled ? "upload" : "plus"} size={18} />
      <span className={labelled ? undefined : "sr-only"}>Add photos</span>
      <input
        className="hidden-input"
        type="file"
        accept="image/*"
        multiple
        disabled={busy}
        onChange={(event) => {
          void addPhotos(event.target.files);
          event.target.value = "";
        }}
      />
    </label>
  );

  // Straight to the rear camera. On a desktop `capture` means nothing and this
  // would be a second file picker, so it is only shown where a finger is the
  // pointer (see PhotoPicker for why the camera and the library are two buttons).
  const cameraButton = (labelled: boolean, className: string) => (
    <button
      type="button"
      className={`${className} touch-only`}
      title="Take a photo"
      disabled={busy}
      onClick={() => camera.current?.click()}
    >
      <Icon name="camera" size={18} />
      <span className={labelled ? undefined : "sr-only"}>Take photo</span>
    </button>
  );

  // A file dragged from the desktop lands anywhere on the photos. Only a drag
  // that carries files lights it up: dragging a thumbnail around the page is
  // not an upload.
  const carriesFiles = (event: React.DragEvent) => event.dataTransfer.types.includes("Files");
  const dropZone = {
    onDragOver: (event: React.DragEvent) => {
      if (!carriesFiles(event)) return;
      event.preventDefault();
      event.dataTransfer.dropEffect = full || busy ? "none" : "copy";
      setDragging(true);
    },
    onDragLeave: (event: React.DragEvent) => {
      // Leaving for a child is not leaving.
      if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setDragging(false);
    },
    onDrop: (event: React.DragEvent) => {
      if (!carriesFiles(event)) return;
      event.preventDefault();
      setDragging(false);
      void addPhotos(event.dataTransfer.files);
    },
  };

  return (
    <>
      <PageHeader
        back={back}
        title={item.title || <span className="untitled">Untitled item</span>}
        meta={
          <>
            {item.status !== "keep" && (
              <span className={`pill pill-status-${item.status}`}>{STATUS_LABELS[item.status]}</span>
            )}
            {item.quantity > 1 && <span className="pill">× {item.quantity}</span>}
            {item.updatedBy && (
              <span>
                Edited by {item.updatedBy.name || item.updatedBy.email}, {touchedAt(item.updatedBy.at)}
              </span>
            )}
          </>
        }
        actions={
          <>
            <button
              type="button"
              className="button button-quiet"
              aria-label="Fill in details"
              disabled={busy}
              onClick={onEnrich}
            >
              <Icon name="sparkles" size={18} />
              <span className="hide-sm">Fill in details</span>
            </button>
            {stock ? (
              stockItem.primary && (
                <button
                  type="button"
                  className="button"
                  disabled={busy}
                  onClick={() => {
                    // The form for it opens under the item and scrolls itself
                    // into view; one already open is only brought back to it.
                    revealPanel(document.querySelector("#stock-panel .panel-reveal"));
                    stockItem.primary?.run();
                  }}
                >
                  <Icon name={stockItem.primary.label === "Deploy" ? "external" : "arrow-left"} size={18} />
                  <span>{stockItem.primary.label}</span>
                </button>
              )
            ) : (
              <button type="button" className="button" disabled={busy} onClick={onListing}>
                <Icon name="bag" size={18} />
                <span>Sell</span>
              </button>
            )}
            <Menu
              label="More actions"
              buttonClassName="icon-button icon-button-bordered"
              button={<Icon name="more" size={20} />}
            >
              {(close) => (
                <>
                  <MenuItem
                    icon={<Icon name="qr" />}
                    onClick={() => {
                      close();
                      onLabel();
                    }}
                  >
                    Print a label
                  </MenuItem>
                  {stock && (
                    <MenuItem
                      icon={<Icon name="bag" />}
                      onClick={() => {
                        close();
                        onListing();
                      }}
                    >
                      Sell
                    </MenuItem>
                  )}
                  <MenuItem
                    danger
                    icon={<Icon name="trash" />}
                    onClick={() => {
                      close();
                      void deleteItem();
                    }}
                  >
                    Delete item
                  </MenuItem>
                </>
              )}
            </Menu>
          </>
        }
      />

      {error && <Notice tone="error">{error}</Notice>}

      <div className={photoSize === "small" ? "item-layout item-layout-compact" : "item-layout"}>
        <section
          className={dragging ? "gallery gallery-dragging" : "gallery"}
          aria-label="Photos"
          {...dropZone}
        >
          <input
            ref={camera}
            className="hidden-input"
            type="file"
            accept="image/*"
            capture="environment"
            tabIndex={-1}
            onChange={(event) => {
              void addPhotos(event.target.files);
              event.target.value = "";
            }}
          />
          {dragging && (
            <p className="gallery-drop" aria-hidden="true">
              <Icon name="upload" size={22} />
              {full ? "This item has all the photos it can hold" : "Drop to add"}
            </p>
          )}
          {current ? (
            <>
              <div className="gallery-main">
                <img src={photoPath(current.id)} alt={`Photo ${at + 1} of ${item.photos.length}`} />
                {at === 0 && (
                  <span className="shot-badge">
                    <Icon name="star" size={12} />
                    Cover
                  </span>
                )}
                <button
                  type="button"
                  className="icon-button gallery-size"
                  aria-label={photoSize === "small" ? "Make the photo larger" : "Make the photo smaller"}
                  title={photoSize === "small" ? "Larger photo" : "Smaller photo"}
                  onClick={() => setPhotoSize(photoSize === "small" ? "large" : "small")}
                >
                  <Icon name={photoSize === "small" ? "maximize" : "minimize"} size={16} />
                </button>
                <div className="gallery-tools" role="group" aria-label="This photo">
                  <button
                    type="button"
                    className="icon-button"
                    aria-label={`Move earlier${at === 1 ? ", making it the cover" : ""}`}
                    title="Move earlier"
                    disabled={busy || at === 0}
                    onClick={() => move(-1)}
                  >
                    <Icon name="chevron-left" size={18} />
                  </button>
                  <button
                    type="button"
                    className="icon-button"
                    aria-label="Make this the cover"
                    title="Make cover"
                    disabled={busy || at === 0}
                    onClick={makeCover}
                  >
                    <Icon name="star" size={18} />
                  </button>
                  <button
                    type="button"
                    className="icon-button"
                    aria-label="Move later"
                    title="Move later"
                    disabled={busy || at === item.photos.length - 1}
                    onClick={() => move(1)}
                  >
                    <Icon name="chevron-right" size={18} />
                  </button>
                  <a
                    className="icon-button"
                    href={photoPath(current.id)}
                    target="_blank"
                    rel="noreferrer"
                    aria-label="Open full size"
                    title="Open full size"
                  >
                    <Icon name="external" size={18} />
                  </a>
                  <button
                    type="button"
                    className="icon-button icon-button-danger"
                    aria-label={`Delete photo ${at + 1}`}
                    title="Delete photo"
                    disabled={busy}
                    onClick={() => void removePhoto(current.id)}
                  >
                    <Icon name="trash" size={18} />
                  </button>
                </div>
              </div>

              <ul className="gallery-thumbs">
                {item.photos.map((photo, index) => (
                  <li key={photo.id}>
                    <button
                      type="button"
                      className="gallery-thumb"
                      aria-label={`Show photo ${index + 1}`}
                      aria-pressed={index === at}
                      onClick={() => setShown(index)}
                    >
                      <img src={thumbPath(photo.id)} alt="" loading="lazy" />
                    </button>
                  </li>
                ))}
                {!full && (
                  <>
                    <li className="touch-only">{cameraButton(false, "gallery-thumb gallery-add")}</li>
                    <li>{addButton(false, "gallery-thumb gallery-add")}</li>
                  </>
                )}
              </ul>
            </>
          ) : (
            <div className="gallery-empty">
              <Icon name="image" size={28} />
              <p>No photos yet</p>
              <p className="gallery-hint pointer-only">Drop photos here, or:</p>
              <div className="gallery-empty-buttons">
                {cameraButton(true, "button")}
                {addButton(true, "button button-quiet button-file")}
              </div>
            </div>
          )}
        </section>

        <div className="item-form">
          <ItemForm
            draft={draft}
            onChange={setDraft}
            taxonomy={taxonomy}
            vocabulary={vocabulary}
            expanded
            costHint={costHint}
            serial={stock}
          />
        </div>
      </div>

      {stock && (
        <div id="stock-panel">
          <StockPanel
            item={item}
            model={stockItem}
            onChanged={() => {
              // A stock write bumps the item's own `updatedAt`. Take only that,
              // so an unsaved edit in the form above is not thrown away.
              void api.items
                .get(item.id)
                .then(({ item: fresh }) =>
                  setItem((current) => (current ? { ...current, updatedAt: fresh.updatedAt } : fresh)),
                );
            }}
          />
        </div>
      )}

      {dirty && (
        <div className="action-bar action-bar-float" data-unsaved>
          <p className="action-bar-status">Unsaved changes</p>
          <button type="button" className="button button-quiet" disabled={busy} onClick={() => adopt(item)}>
            Discard
          </button>
          <button type="button" className="button" disabled={busy} onClick={() => void save()}>
            {busy ? "Saving…" : "Save"}
          </button>
        </div>
      )}
    </>
  );
}
